import { isViewMode } from "../contracts";
import type {
    ActionDef,
    ActionLoad,
    AppEntry,
    BootstrapPayload,
    ClientActionContainerElement,
    ClientActionElement,
    ClientActionRenderPlan,
    ClientActionRibbonElement,
    FieldDef,
    LanguageRef,
    GroupByOption,
    KanbanDragMode,
    KanbanNode,
    KanbanPlan,
    CockpitButton,
    FormCockpitComponent,
    FormCockpitElement,
    FormElement,
    FormKpiElement,
    FormRenderPlan,
    FormStatusbarStage,
    FormSummaryElement,
    FormSummaryToken,
    ListColumn,
    ListRenderPlan,
    ListRowButton,
    MenuEntry,
    OrganizationRef,
    SavedFilter,
    SearchField,
    SearchFilter,
    SearchPlan,
    TextDirection,
    UserIdentity,
    ViewMode,
} from "../contracts";

// Defensive mappers from raw backend (snake_case) JSON to typed contracts.
// Keeping the mapping in one place means a wire-format tweak touches only here.
type Raw = Record<string, unknown>;

const asRaw = (value: unknown): Raw => (value && typeof value === "object" ? (value as Raw) : {});
const str = (value: unknown, fallback = ""): string => (typeof value === "string" ? value : fallback);
const num = (value: unknown, fallback = 0): number => (typeof value === "number" ? value : fallback);
const bool = (value: unknown, fallback = false): boolean => (typeof value === "boolean" ? value : fallback);
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const optStr = (value: unknown): string | undefined => (value == null ? undefined : str(value));
const bySequence = <T extends { sequence?: number }>(a: T, b: T): number => (a.sequence ?? 0) - (b.sequence ?? 0);
const toViewMode = (value: unknown): ViewMode | undefined => {
    const v = str(value);
    return isViewMode(v) ? v : undefined;
};
const viewModeList = (value: unknown): ViewMode[] => list(value).map((v) => str(v)).filter(isViewMode);

export function mapOrganization(raw: unknown): OrganizationRef {
    const o = asRaw(raw);
    return {
        id: str(o.id ?? o.record_uuid),
        code: str(o.code),
        name: str(o.name),
        country: optStr(o.country),
        countryCode: optStr(o.country_code),
        timezone: optStr(o.timezone),
        imageAvatar: o.image_avatar == null ? null : str(o.image_avatar),
        parentId: o.parent_id == null ? null : str(o.parent_id),
    };
}

export function mapUserIdentity(raw: unknown): UserIdentity {
    const o = asRaw(raw);
    // Identity fields (name/email/timezone/language/avatar) live on the nested
    // `user` object; fall back to top-level keys for older payload shapes.
    const u = asRaw(o.user);
    const languageId = u.language_id ?? o.language_id;
    const imageAvatar = u.image_avatar ?? o.image_avatar;
    return {
        userId: str(o.user_id ?? u.record_uuid ?? o.id),
        name: str(u.name ?? o.name ?? u.username ?? o.username),
        email: optStr(u.email ?? o.email),
        timezone: optStr(u.timezone ?? o.timezone),
        languageId: languageId == null ? null : str(languageId),
        imageAvatar: imageAvatar == null ? null : str(imageAvatar),
        allowedOrganizations: list(o.allowed_organizations).map(mapOrganization),
        activeOrganizationId: str(o.active_organization_id),
        roles: list(o.roles).map((r) => str(r)).filter((r) => r.length > 0),
    };
}

// A menu's action lives in a nested `action` object on the wire (slug + name +
// default_view); the slug is the navigable key used in the URL and OPEN_ACTION.
export function mapActionDef(raw: unknown): ActionDef {
    const o = asRaw(raw);
    return {
        key: str(o.slug ?? o.key ?? o.id),
        name: str(o.name),
        modelKey: optStr(o.model_key),
        defaultView: toViewMode(o.default_view),
        availableViews: viewModeList(o.available_views),
        clientComponent: o.client_component == null ? null : str(o.client_component),
        domain: optStr(o.domain) ?? undefined,
        target: (() => {
            const t = optStr(o.target);
            if (t === "dialog" || t === "inline" || t === "current") return t;
            return undefined;
        })(),
    };
}

// ── Action load ──────────────────────────────────────────────────────────────
// Humanize a raw field name into a column label when neither the view nor the
// field-def supplies one ("member_type" → "Member Type", "country_id" → "Country").
function humanize(name: string): string {
    return name
        .replace(/_id$/, "")
        .replace(/[_.]/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim();
}

export function mapFieldDef(raw: unknown): FieldDef {
    const o = asRaw(raw);
    const selection = Array.isArray(o.selection)
        ? o.selection
              .filter((p): p is unknown[] => Array.isArray(p))
              .map((p) => [str(p[0]), str(p[1])] as [string, string])
        : undefined;
    return {
        name: str(o.name),
        type: str(o.type),
        label: optStr(o.string ?? o.label),
        required: bool(o.required),
        readonly: bool(o.readonly),
        selection,
        // Reference / many2one fields ship their target as `ref_model`;
        // one2many / many2many ship it as `target_model` (see FieldSpec.to_ui_dict).
        // Normalise both into `targetModel` so consumers (e.g. the relation
        // back-ref auto-skip) can rely on it for every relational field type.
        targetModel: optStr(o.target_model ?? o.ref_model),
        workflow: o.workflow === true || undefined,
        defaults: o.defaults === undefined ? undefined : (o.defaults as FieldDef["defaults"]),
        // foundation.customization Phase 4 — custom-property markers; when set,
        // the value lives at record[bag][propertyKey].
        isProperty: o.is_property === true || undefined,
        bag: optStr(o.bag),
        propertyKey: optStr(o.property_key),
        help: optStr(o.help),
    };
}

function mapListColumn(raw: unknown, labelByName: Map<string, string>): ListColumn {
    const o = asRaw(raw);
    const name = str(o.name);
    return {
        name,
        label: str(o.string) || labelByName.get(name) || humanize(name),
        widget: optStr(o.widget),
        optional: optStr(o.optional),
        invisible: bool(o.invisible),
        displayFormat: optStr(o.display_format),
        options: mapWidgetOptions(o.options),
    };
}

// Backend `_parse_field_element` collects `option-*` attributes into an
// `options` dict on each field. Mirror that on the frontend as a plain
// Record<string,string> — ignore non-string values defensively.
function mapWidgetOptions(raw: unknown): Record<string, string> | undefined {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
    const entries = Object.entries(raw as Record<string, unknown>).filter(
        (e): e is [string, string] => typeof e[1] === "string",
    );
    if (entries.length === 0) return undefined;
    return Object.fromEntries(entries);
}

export function mapListPlan(raw: unknown, fieldDefs: FieldDef[]): ListRenderPlan | null {
    if (!raw || typeof raw !== "object") return null;
    const o = asRaw(raw);
    const labelByName = new Map(fieldDefs.map((f) => [f.name, f.label ?? f.name]));
    const defaultGroupRaw = str(o.default_group_by).trim();
    const defaultGroupBy = defaultGroupRaw
        ? defaultGroupRaw.split(",").map((s) => s.trim()).filter(Boolean)
        : undefined;
    const editableRaw = str(o.editable).trim().toLowerCase();
    const editable: "top" | "bottom" | "" = editableRaw === "top" || editableRaw === "bottom" ? editableRaw : "";
    return {
        viewType: "list",
        modelKey: str(o.model ?? o.model_key),
        viewId: optStr(o.view_id),
        viewParentId: o.view_parent_id == null ? null : str(o.view_parent_id),
        orderBy: optStr(o.order_by),
        defaultGroupBy,
        columns: list(o.fields).map((f) => mapListColumn(f, labelByName)),
        rowButtons: list(o.row_buttons).map(mapListRowButton),
        editable,
    };
}

function mapListRowButton(raw: unknown): ListRowButton {
    const o = asRaw(raw);
    return {
        name: str(o.name),
        label: optStr(o.label),
        icon: optStr(o.icon),
        command: optStr(o.command),
        commandType: mapCommandType(o.command_type),
        special: optStr(o.special),
        context: optStr(o.context),
        invisible: optStr(o.invisible),
        confirm: optStr(o.confirm),
    };
}

// `command-type` is an open-ended discriminator; we whitelist the engines the
// frontend knows how to route. Unknown values fall back to null so the button
// goes through the default service-api dispatcher.
function mapCommandType(raw: unknown): import("../contracts").ButtonCommandType | null {
    const v = optStr(raw);
    if (v === "workflow" || v === "approval" || v === "report" || v === "action") return v;
    return null;
}

function mapSearchPlan(raw: unknown): SearchPlan {
    const o = asRaw(raw);
    const searchfields: SearchField[] = list(o.searchfields).map((f) => {
        const r = asRaw(f);
        return {
            name: str(r.name),
            label: str(r.label ?? r.string ?? r.name),
            filterDomain: str(r.filter_domain ?? r.filterDomain ?? r.domain),
        };
    });
    const filters: SearchFilter[] = list(o.filters).map((f) => {
        const r = asRaw(f);
        return {
            key: str(r.name ?? r.key),
            label: str(r.label ?? r.string ?? r.name),
            domain: str(r.domain ?? r.filter_domain),
        };
    });
    const groupbys: GroupByOption[] = list(o.groupbys).map((g) => {
        const r = asRaw(g);
        // Parser emits `fields: string[]` (from `groups="a,b"` on <groupby>) —
        // fall back to a single-element list for legacy single-field shapes.
        const raw = r.fields ?? r.field ?? r.name;
        const fields = Array.isArray(raw)
            ? raw.map((f) => str(f)).filter(Boolean)
            : [str(raw)].filter(Boolean);
        return { fields, label: str(r.label ?? r.string ?? r.name) };
    });
    return { searchfields, filters, groupbys, hasActive: bool(o.has_active) };
}

function mapKanbanNode(raw: unknown): KanbanNode {
    const o = asRaw(raw);
    const node: KanbanNode = { type: str(o.type) };
    if (o.name != null) node.name = str(o.name);
    if (o.widget != null) node.widget = str(o.widget);
    if (o.invisible != null) node.invisible = bool(o.invisible);
    if (o.attrs && typeof o.attrs === "object") node.attrs = o.attrs as Record<string, unknown> as KanbanNode["attrs"];
    if (Array.isArray(o.children)) node.children = o.children.map(mapKanbanNode);
    return node;
}

// ── Form RenderPlan mapping ─────────────────────────────────────────────────
// Walks the recursive element tree the backend emits (`<header>` / `<sheet>` /
// `<footer>`). Each element gets normalised into a tagged-union variant so
// React renderers can branch on `type` without re-parsing the wire shape.

function mapFormStatusbarStage(raw: unknown): FormStatusbarStage {
    const o = asRaw(raw);
    // Backend ships `is_terminal` on the wire; legacy / external callers may
    // send `is_final` — read both for tolerance.
    const terminalRaw = o.is_terminal ?? o.is_final;
    return {
        code: str(o.code),
        label: str(o.label) || str(o.code),
        isInitial: o.is_initial == null ? undefined : bool(o.is_initial),
        isFinal: terminalRaw == null ? undefined : bool(terminalRaw),
    };
}

function mapFormElement(raw: unknown): FormElement | null {
    const o = asRaw(raw);
    const type = str(o.type);
    switch (type) {
        case "field": {
            const colspanRaw = o.colspan;
            return {
                type: "field",
                name: str(o.name),
                string: optStr(o.string),
                widget: optStr(o.widget),
                invisible: optStr(o.invisible),
                readonly: o.readonly == null ? undefined : bool(o.readonly),
                required: o.required == null ? undefined : bool(o.required),
                colspan: typeof colspanRaw === "number" ? colspanRaw : undefined,
                nolabel: o.nolabel == null ? undefined : bool(o.nolabel),
                cssClass: optStr(o.css_class),
                placeholder: optStr(o.placeholder),
                displayFormat: optStr(o.display_format),
                domain: optStr(o.domain),
                // Inline `<ListView>` declared under `<field>` — parsed as a
                // regular list plan by the backend. Labels are resolved at
                // render-time inside RelationListView using the child model's
                // field defs (fetched on demand), so we pass an empty fieldDefs
                // list here. Falls back to a humanised column name if the
                // child's fieldDef lookup misses.
                inlineListView: o.inline_list_view ? mapListPlan(o.inline_list_view, []) : null,
                options: mapWidgetOptions(o.options),
            };
        }
        case "button": {
            const specialOpts = asRaw(o.special_options);
            const opts: Record<string, string> = {};
            for (const [k, v] of Object.entries(specialOpts)) {
                if (typeof v === "string") opts[k] = v;
            }
            return {
                type: "button",
                name: str(o.name),
                command: optStr(o.command),
                commandType: mapCommandType(o.command_type),
                label: optStr(o.label),
                labelField: optStr(o.label_field),
                icon: optStr(o.icon),
                invisible: optStr(o.invisible),
                context: optStr(o.context),
                confirm: optStr(o.confirm),
                special: optStr(o.special),
                specialOptions: Object.keys(opts).length ? opts : undefined,
            };
        }
        case "section": {
            return {
                type: "section",
                cols: typeof o.cols === "number" ? o.cols : null,
                string: optStr(o.string),
                invisible: optStr(o.invisible),
                elements: list(o.elements).map(mapFormElement).filter((e): e is FormElement => e !== null),
            };
        }
        case "notebook": {
            const pages = list(o.pages).map((p) => {
                const pr = asRaw(p);
                return {
                    type: "page" as const,
                    label: str(pr.label),
                    icon: optStr(pr.icon),
                    invisible: optStr(pr.invisible),
                    elements: list(pr.elements).map(mapFormElement).filter((e): e is FormElement => e !== null),
                };
            });
            return { type: "notebook", pages };
        }
        case "statusbar": {
            return {
                type: "statusbar",
                field: str(o.field),
                workflowCode: optStr(o.workflow_code),
                allStages: list(o.all_stages).map(mapFormStatusbarStage),
            };
        }
        case "decision": {
            return {
                type: "decision",
                title: optStr(o.title),
                invisible: optStr(o.invisible),
            };
        }
        case "separator":
            return {
                type: "separator",
                string: optStr(o.string),
                id: optStr(o.id),
                invisible: optStr(o.invisible),
            };
        case "property":
            return {
                type: "property",
                name: str(o.name),
                bag: str(o.bag),
                key: str(o.key),
                string: optStr(o.string),
                widget: optStr(o.widget),
                invisible: optStr(o.invisible),
                readonly: o.readonly === true,
                required: o.required === true ? true : o.required === false ? false : null,
                nolabel: o.nolabel === true,
                placeholder: optStr(o.placeholder),
                options: mapWidgetOptions(o.options),
            };
        case "related": {
            const items = list(o.items).map((rawItem) => {
                const r = asRaw(rawItem);
                return {
                    name: str(r.name),
                    action: optStr(r.action),
                    targetModelKey: optStr(r.target_model_key),
                    fieldLabel: optStr(r.field_label),
                    icon: optStr(r.icon),
                    string: optStr(r.string),
                    hideEmpty: r.hide_empty == null ? true : bool(r.hide_empty),
                };
            });
            return { type: "related", items };
        }
        default:
            return null;
    }
}

function mapSummaryToken(raw: unknown): FormSummaryToken | null {
    const o = asRaw(raw);
    if (o.type === "field") {
        const name = str(o.name);
        return name
            ? { type: "field", name, displayFormat: optStr(o.display_format), icon: optStr(o.icon), chip: optStr(o.chip) }
            : null;
    }
    if (o.type === "text") {
        return { type: "text", value: str(o.value) };
    }
    return null;
}

function mapSummary(raw: unknown): FormSummaryElement | null {
    if (!raw || typeof raw !== "object") return null;
    const o = asRaw(raw);
    const kpis: FormKpiElement[] = list(o.kpis).map((k) => {
        const kr = asRaw(k);
        return {
            field: kr.field == null ? null : str(kr.field),
            metric: kr.metric == null ? null : str(kr.metric),
            formula: kr.formula == null ? null : str(kr.formula),
            label: kr.label == null ? null : str(kr.label),
            emphasis: kr.emphasis == null ? null : str(kr.emphasis),
        };
    });
    return {
        eyebrow: list(o.eyebrow).map(mapSummaryToken).filter((t): t is FormSummaryToken => t !== null),
        title: list(o.title).map(mapSummaryToken).filter((t): t is FormSummaryToken => t !== null),
        subtitle: list(o.subtitle).map(mapSummaryToken).filter((t): t is FormSummaryToken => t !== null),
        kpis,
    };
}

function mapCockpitButton(raw: unknown): CockpitButton {
    const o = asRaw(raw);
    return {
        command: o.command == null ? null : str(o.command),
        clientAction: o.client_action == null ? null : str(o.client_action),
        label: o.label == null ? null : str(o.label),
        icon: o.icon == null ? null : str(o.icon),
        invisible: o.invisible == null ? null : str(o.invisible),
        confirm: o.confirm == null ? null : str(o.confirm),
    };
}

function mapCockpitComponent(raw: unknown): FormCockpitComponent | null {
    const o = asRaw(raw);
    switch (o.type) {
        case "status":
            return {
                type: "status",
                field: o.field == null ? null : str(o.field),
                showAge: o.show_age == null ? true : bool(o.show_age),
                buttons: list(o.buttons).map(mapCockpitButton),
            };
        case "quick-actions":
            return { type: "quick-actions", actions: list(o.actions).map(mapCockpitButton) };
        case "checklist":
            return {
                type: "checklist",
                stageGate: o.stage_gate == null ? true : bool(o.stage_gate),
                ref: o.ref == null ? null : str(o.ref),
            };
        case "card": {
            const attrs: Record<string, string> = {};
            const rawAttrs = asRaw(o.attrs);
            for (const k of Object.keys(rawAttrs)) attrs[k] = str(rawAttrs[k]);
            return { type: "card", key: o.key == null ? null : str(o.key), attrs };
        }
        case "decision":
        case "assistant":
        case "suggestions":
        case "tasks":
            return { type: o.type };
        default:
            return null;
    }
}

function mapCockpit(raw: unknown): FormCockpitElement | null {
    if (!raw || typeof raw !== "object") return null;
    const o = asRaw(raw);
    return {
        auto: o.auto == null ? true : bool(o.auto),
        width: o.width == null ? null : str(o.width),
        components: list(o.components)
            .map(mapCockpitComponent)
            .filter((c): c is FormCockpitComponent => c !== null),
    };
}

export function mapFormPlan(raw: unknown): FormRenderPlan | null {
    if (!raw || typeof raw !== "object") return null;
    const o = asRaw(raw);
    return {
        viewType: "form",
        modelKey: str(o.model ?? o.model_key),
        viewId: optStr(o.view_id),
        viewParentId: o.view_parent_id == null ? null : str(o.view_parent_id),
        create: o.create == null ? true : bool(o.create),
        edit: o.edit == null ? true : bool(o.edit),
        delete: o.delete == null ? true : bool(o.delete),
        header: list(o.header).map(mapFormElement).filter((e): e is FormElement => e !== null),
        summary: mapSummary(o.summary),
        cockpit: mapCockpit(o.cockpit),
        sheet: list(o.sheet).map(mapFormElement).filter((e): e is FormElement => e !== null),
        footer: list(o.footer).map(mapFormElement).filter((e): e is FormElement => e !== null),
        activity: list(o.activity).map((c) => str(c)),
    };
}

// ── ClientAction layout (Enhancement 08 — layout-XML slice) ────────────────
// Maps the backend's `<ClientActionView>` render plan to the typed
// `ClientActionRenderPlan`. Buttons delegate to `mapFormElement` (returns
// `FormButtonElement` for the button branch — assignable to
// `ClientActionElement`); other element types (container/component/hint/
// ribbon) are mapped inline.

function mapClientActionElement(raw: unknown): ClientActionElement | null {
    const o = asRaw(raw);
    const type = str(o.type);
    switch (type) {
        case "button": {
            // Delegate to the shared form-button mapper. The returned
            // FormButtonElement is assignable to ClientActionElement
            // by union membership.
            const el = mapFormElement(raw);
            return el && el.type === "button" ? el : null;
        }
        case "container": {
            const ref = str(o.ref);
            if (!ref) return null;
            const out: ClientActionContainerElement = { type: "container", ref };
            const cls = optStr(o.class);
            if (cls) out.class = cls;
            return out;
        }
        case "component":
            return { type: "component" };
        case "hint":
            return { type: "hint", text: str(o.text) };
        case "ribbon": {
            const toneRaw = optStr(o.tone);
            const tone = toneRaw && ["success", "info", "warning", "danger", "neutral"].includes(toneRaw)
                ? (toneRaw as ClientActionRibbonElement["tone"])
                : undefined;
            const out: ClientActionRibbonElement = {
                type: "ribbon",
                label: str(o.label),
            };
            if (tone) out.tone = tone;
            const vw = optStr(o.visible_when);
            const hw = optStr(o.hidden_when);
            if (vw) out.visibleWhen = vw;
            if (hw) out.hiddenWhen = hw;
            return out;
        }
        default:
            return null;
    }
}

export function mapClientActionPlan(raw: unknown): ClientActionRenderPlan | null {
    if (!raw || typeof raw !== "object") return null;
    const o = asRaw(raw);
    const sizeRaw = optStr(o.size);
    const size = sizeRaw && ["sm", "md", "lg", "xl", "full"].includes(sizeRaw)
        ? (sizeRaw as ClientActionRenderPlan["size"])
        : undefined;
    const titleRaw = optStr(o.title);
    return {
        viewType: "clientaction",
        modelKey: str(o.model ?? o.model_key),
        title: titleRaw || undefined,
        size,
        header: list(o.header).map(mapClientActionElement).filter((e): e is ClientActionElement => e !== null),
        body: list(o.body).map(mapClientActionElement).filter((e): e is ClientActionElement => e !== null),
        footer: list(o.footer).map(mapClientActionElement).filter((e): e is ClientActionElement => e !== null),
    };
}

function mapKanbanPlan(raw: unknown): KanbanPlan | null {
    if (!raw || typeof raw !== "object") return null;
    const o = asRaw(raw);
    const drag = str(o.on_drag, "auto");
    return {
        modelKey: str(o.model ?? o.model_key),
        viewId: optStr(o.view_id),
        viewParentId: o.view_parent_id == null ? null : str(o.view_parent_id),
        defaultGroupBy: str(o.default_group_by),
        orderBy: optStr(o.order_by),
        onDrag: (["field", "workflow", "auto"].includes(drag) ? drag : "auto") as KanbanDragMode,
        quickCreate: o.quick_create == null ? true : bool(o.quick_create),
        allowReorder: bool(o.allow_reorder),
        cardTemplate: o.card_template ? mapKanbanNode(o.card_template) : null,
        fieldNames: list(o.field_names).map((n) => str(n)),
    };
}

export function mapSavedFilter(raw: unknown): SavedFilter {
    const o = asRaw(raw);
    const ft = str(o.filter_type ?? o.filterType, "filter");
    return {
        id: str(o.id ?? o.record_uuid),
        name: str(o.name),
        filterType: (["filter", "groupby", "favorite"].includes(ft) ? ft : "filter") as SavedFilter["filterType"],
        domain: str(o.domain, "[]"),
        groupby: optStr(o.groupby),
        isShared: bool(o.is_shared),
    };
}

export function mapActionLoad(raw: unknown): ActionLoad {
    const o = asRaw(raw);
    const fieldDefs = list(o.field_defs).map(mapFieldDef);
    const views = asRaw(o.views);
    const rawVfn = asRaw(o.view_field_names);
    const toStringList = (v: unknown): string[] | undefined =>
        Array.isArray(v) ? v.map((x) => String(x)) : undefined;
    return {
        action: mapActionDef(o.action),
        fieldDefs,
        listPlan: mapListPlan(views.list, fieldDefs),
        searchPlan: mapSearchPlan(views.search),
        kanbanPlan: mapKanbanPlan(views.kanban),
        formPlan: mapFormPlan(views.form),
        viewFieldNames: {
            list: toStringList(rawVfn.list),
            form: toStringList(rawVfn.form),
            kanban: toStringList(rawVfn.kanban),
        },
        boundActions: list(o.bound_actions).map((b) => {
            const r = asRaw(b);
            return {
                slug: String(r.slug ?? ""),
                name: String(r.name ?? ""),
                target: String(r.target ?? "dialog"),
                context: String(r.context ?? ""),
            };
        }),
    };
}

export function mapMenuEntry(raw: unknown): MenuEntry {
    const o = asRaw(raw);
    const action = o.action ? mapActionDef(o.action) : undefined;
    return {
        id: str(o.id ?? o.record_uuid),
        name: str(o.name),
        icon: optStr(o.icon),
        sequence: num(o.sequence),
        actionKey: action?.key ?? optStr(o.action_key),
        action,
        children: list(o.children).map(mapMenuEntry).sort(bySequence),
    };
}

export function mapAppEntry(raw: unknown): AppEntry {
    const o = asRaw(raw);
    return {
        id: str(o.id ?? o.record_uuid),
        key: str(o.key ?? o.id),
        name: str(o.name),
        icon: optStr(o.icon),
        category: optStr(o.category),
        sequence: num(o.sequence),
        color: num(o.color, 10),
    };
}

export function mapBootstrap(raw: unknown): BootstrapPayload {
    const o = asRaw(raw);
    const menusRaw = asRaw(o.menus);
    const menus: Record<string, MenuEntry[]> = {};
    for (const key of Object.keys(menusRaw)) {
        menus[key] = list(menusRaw[key]).map(mapMenuEntry).sort(bySequence);
    }
    const flagsRaw = asRaw(o.feature_flags);
    const featureFlags: Record<string, boolean> = {};
    for (const key of Object.keys(flagsRaw)) {
        featureFlags[key] = bool(flagsRaw[key]);
    }
    return {
        apps: list(o.apps).map(mapAppEntry).sort(bySequence),
        menus,
        pendingApprovalCount: num(o.pending_approval_count),
        featureFlags,
    };
}

export function mapLanguage(raw: unknown): LanguageRef {
    const o = asRaw(raw);
    const direction: TextDirection = str(o.direction, "ltr") === "rtl" ? "rtl" : "ltr";
    return {
        id: str(o.id ?? o.record_uuid),
        code: str(o.code),
        name: optStr(o.name),
        nativeName: optStr(o.native_name),
        direction,
    };
}
